import moment from "moment";
import { getTimezone } from "./getTimezone";

export const GetTenantId = () => {
  return sessionStorage.getItem("tenant_id");
};

export const GetToken = () => {
  return sessionStorage.getItem("token");
};

export const GetRefreshToken = () => {
  return sessionStorage.getItem("refresh_token");
};

export const GetExpiresAtToken = () => {
  return sessionStorage.getItem("expires_at");
};

export const IsSubscriptionValid = () => {
  return sessionStorage.getItem("valid_sub") === "true";
};

export const GetMonthDiff = (dateFrom, dateTo, roundDown = false) => {
  const _MS_PER_MONTH = 1000 * 60 * 60 * 24 * (365.25 / 12);
  const monthDiff = (dateTo - dateFrom) / _MS_PER_MONTH;

  return roundDown ? Math.floor(monthDiff) : monthDiff;
};

/**
 * Function to implement removing an element from DOM with fade out effect.
 * @param {HTMLElement} element element to be removed
 * @param {Number} speed speed in MS for the fade out
 */
export function removeFadeOut(element, speed = 2000) {
  var seconds = speed / 1000;

  element.style.transition = `all ${seconds}s ease`;
  element.style.opacity = 0;

  setTimeout(() => {
    element.remove();
  }, speed);
}

export const getTextColor = (color) => {
  const hex = color?.replace("#", "");

  let r = parseInt(hex?.substr(0, 2), 16);
  let g = parseInt(hex?.substr(2, 2), 16);
  let b = parseInt(hex?.substr(4, 2), 16);

  r /= 255;
  g /= 255;
  b /= 255;

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

export const isGuid = (value) => {
  const regex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return regex.test(value);
};

export const GetFieldType = (fieldType) => {
  const fieldOptions = [
    { title: "ShortText", value: 0, icon: "fa-regular fa-a" },
    { title: "Number", value: 1, icon: "fa-regular fa-hashtag" },
    { title: "Date", value: 2, icon: "fa-regular fa-calendar" },
    { title: "YesNo", value: 3, icon: "fa-regular fa-check" },
  ];

  return fieldOptions.find((option) => option.value === fieldType);
};

export const isTruthy = (value) => {
  return value ? true : false;
};

export const isTruthyString = (value) => {
  return value == "true" ? true : false;
};

export const isDefined = (value) => {
  if (value !== undefined && value != null) {
    return true;
  } else {
    return false;
  }
};

export const RemoveAccents = (string) => {
  return string.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const IsEmptyObject = (obj) => {
  return obj.constructor === Object && Object.keys(obj).length === 0;
};

export const GetFullName = (firstName, initial, lastName, secondLastName) => {
  let name = firstName;

  if (initial) {
    name += " " + initial + ".";
  }

  name += " " + lastName;

  if (secondLastName) {
    name += " " + secondLastName;
  }

  return name;
};

export const ExtractPhoneNumber = (str) => {
  const regex = /(\d{3})[- ]?(\d{3})[- ]?(\d{4})/;
  const matches = str.match(regex);
  if (matches) {
    return {
      areaCode: matches[1],
      prefix: matches[2],
      lineNumber: matches[3],
    };
  }
  return null;
};

export const dispatchEvent = (type, detail) => {
  const event = new CustomEvent(type, {
    detail: detail,
  });

  window.dispatchEvent(event);
};

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const formatUSD = (price) => {
  return USDollar.format(price);
};

export const waitForElm = (selector) => {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
};

export const formatPercent = (num = 0, decimals = 2) => {
  return Number(num / 100).toLocaleString(undefined, {
    style: "percent",
    maximumFractionDigits: decimals,
  });
};

export const arrayBufferToBlob = (buffer, type, name) => {
  return new Blob([buffer], { type: type, name: name });
};

export const formatUnixDate = (value, formatType) => {
  let momentDate = moment.unix(value);
  return momentDate.format(formatType);
};

export const formatDate = (value, formatType, isUTC = true) => {
  const timezone = getTimezone();
  let momentDate = moment.utc(value).tz(timezone);

  if (!isUTC) momentDate = moment(value).tz(timezone);

  return momentDate.format(formatType);
};

export const randomString = (length) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

/**
 * Converts a base64 string to a Uint8Array.
 *
 * @param {string} base64String - The base64 string to convert.
 * @returns {Uint8Array} - The converted Uint8Array.
 */
export const base64ToBytes = (base64String) => {
  const binary_string = window.atob(base64String);
  const len = base64String.length;

  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }

  return bytes;
};

export const downloadPdf = (data, fileName) => {
  const binary_string = window.atob(data);
  const len = data.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }

  const blob = new Blob([bytes.buffer], {
    type: "application/pdf",
  });

  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${fileName.replaceAll(" ", "_")}.pdf`;

  document.body.appendChild(anchor);

  anchor.click();

  document.body.removeChild(anchor);

  window.URL.revokeObjectURL(url);
};

export const download = async (url) => {
  const link = document.createElement("a");

  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getSiblingsHeight = (elm) => {
  let response = 0;
  while (elm) {
    response += elm.clientHeight;
    elm = elm?.previousSibling;
  }

  return response;
};

export async function urltoFile(url, filename, mimeType) {
  if (url.startsWith("data:")) {
    var arr = url.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    var file = new File([u8arr], filename, { type: mime || mimeType });
    return Promise.resolve(file);
  }
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  return new File([buf], filename, { type: mimeType });
}

export async function getInitials(name) {
  if (!name) return "";
  const names = name.split(" ");
  if (names.length === 1 && names[0].length > 1) {
    return names[0].substring(0, 2).toUpperCase();
  } else {
    return names
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }
}

export function formatDateFromNow(date, locale, defaultFormat) {
  const momentDate = moment.utc(date).tz(getTimezone());
  const daysFromNow = moment()
    .locale(locale)
    .startOf("day")
    .diff(moment(momentDate /*Avoid mutating*/).startOf("day"), "days");

  switch (daysFromNow) {
    case 0:
      return momentDate.from();
    case 1:
      return momentDate.calendar();
    default:
      return momentDate.format(defaultFormat);
  }
}

export function isNullOrUndefined(value) {
  return value === null || value === undefined;
}

export function removObjects(left, right, comparer) {
  if (!comparer) {
    if (typeof left === "object" && typeof right === "object")
      comparer = (left, right) =>
        (left.id ?? left.Id) === (right.id ?? right.Id);
  }

  return left.filter(
    (leftValue) => !right.some((rightValue) => comparer(leftValue, rightValue))
  );
}

/**
 * This function caps the day to the last/first day of the month.
 * In JS , If the day exceeds(or precedes) the number of days in the month,
 * it will roll over to the next month (e.g. 2025-01-32 will become 2025-02-02).
 *
 * @link [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/setDate#:~:text=If%20you%20specify%20a%20number%20outside%20the%20expected%20range)
 * @returns a Date object with the day adjusted to fit within the month if necessary.
 */
export const safeCreateDate = (year, month, day) => {
  const date = new Date(year, month, day);
  const firstDayOfMonth = moment(new Date(year, month, 1)).locale("en");
  const lastDayOfMonth = moment(firstDayOfMonth.toDate())
    .endOf("month")
    .locale("en");
  if (date > lastDayOfMonth) {
    return lastDayOfMonth.toDate();
  } else if (date < firstDayOfMonth) {
    return firstDayOfMonth.toDate();
  } else {
    return date;
  }
};

export const canModifyTask = (task, userId, rolePermissions) =>
  Boolean(
    !rolePermissions?.CanModifyTasks ||
    (!task?.Assignee && (task?.Collaborators?.length ?? 0) === 0) ||
    task?.Assignee?.Id === userId ||
    (task?.Collaborators?.some((x) => x.UserId === userId) ?? false)
  );
